import { useMemo } from "react";
import { motion } from "framer-motion";

// ─── SVG ARC HELPERS ────────────────────────────────────
function polar(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, outerR, innerR, startDeg, endDeg) {
  const os = polar(cx, cy, outerR, endDeg);
  const oe = polar(cx, cy, outerR, startDeg);
  const is_ = polar(cx, cy, innerR, startDeg);
  const ie = polar(cx, cy, innerR, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${os.x} ${os.y} A ${outerR} ${outerR} 0 ${large} 0 ${oe.x} ${oe.y} L ${is_.x} ${is_.y} A ${innerR} ${innerR} 0 ${large} 1 ${ie.x} ${ie.y} Z`;
}

function midArcPos(cx, cy, r, startDeg, endDeg) {
  return polar(cx, cy, r, (startDeg + endDeg) / 2);
}

// Semicircle arc path for textPath (top half: left→right through top)
function topArcPath(cx, cy, r) {
  return `M ${cx - r},${cy} A ${r},${r} 0 1,1 ${cx + r},${cy}`;
}
// Bottom half arc (right→left through bottom) for upright text
function bottomArcPath(cx, cy, r) {
  return `M ${cx + r},${cy} A ${r},${r} 0 1,1 ${cx - r},${cy}`;
}

const VB = 1000;
const PAD = 50;
const CX = VB / 2;
const CY = VB / 2;
const GAP = 1.5;

const ECO_OR = 460;
const ECO_IR = 410;
const SOC_OR = 230;
const SOC_IR = 185;
const DEFICIENCY_IR = 170;

// ─── STATUS COLOR HELPERS ────────────────────────────────
const STATUS_COLORS = {
  exceeding: { r: 239, g: 68, b: 68 },
  approaching: { r: 245, g: 158, b: 11 },
  safe: { r: 74, g: 222, b: 128 },
  neutral: { r: 255, g: 255, b: 255 },
};

function statusRgba(status, alpha) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.safe;
  return `rgba(${c.r},${c.g},${c.b},${alpha})`;
}

function segmentColor(status) {
  if (status === "exceeding") return "exceeding";
  if (status === "approaching") return "approaching";
  return "safe";
}

function getSegmentStyle(status, { isActive, isFocus, isCross, hasActive }) {
  const s = segmentColor(status);
  if (isActive && isFocus) {
    return { fill: statusRgba(s, 0.5), stroke: statusRgba(s, 0.9), opacity: 1 };
  }
  if (isActive) {
    return { fill: statusRgba(s, s === "safe" ? 0.3 : 0.4), stroke: statusRgba(s, s === "exceeding" ? 0.75 : 0.6), opacity: 1 };
  }
  if (isCross) {
    return { fill: "rgba(251,191,36,0.25)", stroke: "rgba(251,191,36,0.55)", opacity: 1 };
  }
  const fillAlpha = s === "exceeding" ? 0.25 : s === "approaching" ? 0.18 : 0.06;
  const strokeAlpha = s === "exceeding" ? 0.5 : s === "approaching" ? 0.4 : 0.15;
  const fillC = s === "safe" ? "neutral" : s;
  const strokeC = s === "safe" ? "neutral" : s;
  return { fill: statusRgba(fillC, fillAlpha), stroke: statusRgba(strokeC, strokeAlpha), opacity: hasActive ? 0.4 : 1 };
}

function getLabelFill(status, { isActive, isCross }) {
  const s = segmentColor(status);
  if (isCross && !isActive) return "rgba(251,191,36,0.7)";
  if (isActive) return statusRgba(s, s === "exceeding" ? 0.95 : 0.9);
  return statusRgba(s === "safe" ? "neutral" : s, s === "exceeding" ? 0.55 : s === "approaching" ? 0.45 : 0.35);
}

// ─── ECOLOGICAL SEGMENT ─────────────────────────────────
function EcoSegment({ seg, index, total, activeCategory, crossCategories, isFocus, allNodes }) {
  const angle = 360 / total;
  const startDeg = index * angle + GAP;
  const endDeg = (index + 1) * angle - GAP;

  const isActive = activeCategory === seg.id;
  const isCross = crossCategories.has(seg.id);
  const isExceeding = seg.status === "exceeding";
  const isApproaching = seg.status === "approaching";
  const isImproving = (seg.linked_nodes || []).some(nid => {
    const n = allNodes?.find(x => x.id === nid);
    return n?.trend === "improving";
  });
  const hasActive = !!activeCategory;

  // Boundary-distance: radial shift for segments exceeding safe threshold
  const safeT = seg.safe_threshold || 0.7;
  const overshoot = Math.max(0, (seg.value - safeT) / safeT);
  const MAX_SHIFT = 65;
  const shift = Math.min(overshoot, 1.0) * MAX_SHIFT;
  const midDeg = (startDeg + endDeg) / 2;
  const midRad = ((midDeg - 90) * Math.PI) / 180;
  const shiftX = shift * Math.cos(midRad);
  const shiftY = shift * Math.sin(midRad);
  // Proportional overshoot band thickness (10–40 SVG units)
  const overshootBand = 10 + Math.min(overshoot, 1.0) * 30;

  let fill, stroke, opacity;
  const style = getSegmentStyle(seg.status, { isActive, isFocus, isCross, hasActive });
  fill = style.fill;
  stroke = style.stroke;
  opacity = style.opacity;

  const lbl = midArcPos(CX, CY, ECO_OR + 40, startDeg, endDeg);

  return (
    <g data-testid={`doughnut-eco-${seg.id}`}>
      {/* Arc group shifts radially outward when exceeding safe boundary */}
      <motion.g
        animate={{ x: shiftX, y: shiftY }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <motion.path
          d={arcPath(CX, CY, ECO_OR, ECO_IR, startDeg, endDeg)}
          fill={fill} stroke={stroke} strokeWidth={isActive ? 2.5 : 1.5}
          animate={{ opacity }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />

        {isExceeding && (
          <motion.path
            d={arcPath(CX, CY, ECO_OR + overshootBand, ECO_OR + 1, startDeg, endDeg)}
            fill="rgba(239,68,68,0.12)" stroke="rgba(239,68,68,0.3)" strokeWidth="0.5"
            animate={{ opacity: isActive ? [0.6, 1, 0.6] : [0.4, 0.7, 0.4] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            filter={isActive ? "url(#glow-red)" : undefined}
            data-testid={`overshoot-${seg.id}`}
          />
        )}

        {isImproving && !isExceeding && (
          <motion.path
            d={arcPath(CX, CY, ECO_OR, ECO_IR, startDeg, endDeg)}
            fill="rgba(74,222,128,0.04)" stroke="none"
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            filter={isActive ? "url(#glow-green)" : undefined}
            data-testid={`improving-eco-${seg.id}`}
          />
        )}
      </motion.g>

      {/* Labels stay at original position (unshifted) */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
        <text
          x={lbl.x} y={lbl.y}
          textAnchor="middle" dominantBaseline="central"
          fill={getLabelFill(seg.status, { isActive, isCross })}
          fontSize={isActive ? "28" : "22"}
          fontFamily="Manrope,sans-serif" fontWeight="700" letterSpacing="0.5"
          data-testid={`eco-label-${seg.id}`}
        >
          {seg.label}
        </text>
        {isActive && seg.overshoot_pct > 0 && (
          <text x={lbl.x} y={lbl.y + 22} textAnchor="middle"
            fill="rgba(239,68,68,0.6)" fontSize="16" fontFamily="Manrope,sans-serif">
            +{seg.overshoot_pct}% overshoot
          </text>
        )}
      </motion.g>
    </g>
  );
}

// ─── SOCIAL SEGMENT ─────────────────────────────────────
function SocSegment({ seg, index, total, activeCategory, crossCategories, isFocus, allNodes }) {
  const angle = 360 / total;
  const startDeg = index * angle + GAP;
  const endDeg = (index + 1) * angle - GAP;

  const isActive = activeCategory === seg.id;
  const isCross = crossCategories.has(seg.id);
  const isBelow = seg.status === "below";
  const isApproaching = seg.status === "approaching";
  const isImproving = (seg.linked_nodes || []).some(nid => {
    const n = allNodes?.find(x => x.id === nid);
    return n?.trend === "improving";
  });
  const hasActive = !!activeCategory;

  let fill, stroke, opacity;
  // Map social statuses to segment style: "below" = warning (like approaching), "above" = safe
  const socialStatus = isBelow ? "approaching" : isApproaching ? "approaching" : "safe";
  const style = getSegmentStyle(socialStatus, { isActive, isFocus, isCross, hasActive });
  fill = style.fill;
  stroke = style.stroke;
  opacity = style.opacity;
  // For "below" in focus mode, use slightly higher alpha
  if (isActive && isFocus && isBelow) {
    fill = "rgba(245,158,11,0.5)";
    stroke = "rgba(245,158,11,0.9)";
  }

  const lbl = midArcPos(CX, CY, SOC_IR - 30, startDeg, endDeg);

  const labelFill = isCross && !isActive ? "rgba(251,191,36,0.7)"
    : isActive ? (isBelow ? "rgba(245,158,11,0.9)" : isApproaching ? "rgba(245,158,11,0.85)" : "rgba(74,222,128,0.85)")
    : isBelow ? "rgba(245,158,11,0.5)" : isApproaching ? "rgba(245,158,11,0.4)" : "rgba(255,255,255,0.35)";

  return (
    <g data-testid={`doughnut-soc-${seg.id}`}>
      <motion.path
        d={arcPath(CX, CY, SOC_OR, SOC_IR, startDeg, endDeg)}
        fill={fill} stroke={stroke} strokeWidth={isActive ? 2.5 : 1.5}
        animate={{ opacity }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      />

      {isBelow && (
        <motion.path
          d={arcPath(CX, CY, SOC_IR, DEFICIENCY_IR, startDeg, endDeg)}
          fill="rgba(245,158,11,0.12)" stroke="rgba(245,158,11,0.3)" strokeWidth="0.5"
          animate={{ opacity: isActive ? [0.6, 1, 0.6] : [0.4, 0.7, 0.4] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          filter={isActive ? "url(#glow-amber)" : undefined}
          data-testid={`deficiency-${seg.id}`}
        />
      )}

      {isImproving && !isBelow && (
        <motion.path
          d={arcPath(CX, CY, SOC_OR, SOC_IR, startDeg, endDeg)}
          fill="rgba(74,222,128,0.04)" stroke="none"
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          filter={isActive ? "url(#glow-green)" : undefined}
          data-testid={`improving-soc-${seg.id}`}
        />
      )}

      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
        <text
          x={lbl.x} y={lbl.y}
          textAnchor="middle" dominantBaseline="central"
          fill={labelFill}
          fontSize={isActive ? "24" : "18"}
          fontFamily="Manrope,sans-serif" fontWeight="700" letterSpacing="0.5"
          data-testid={`soc-label-${seg.id}`}
        >
          {seg.label}
        </text>
        {isActive && seg.deficiency_pct > 0 && (
          <text x={lbl.x} y={lbl.y + 20} textAnchor="middle"
            fill="rgba(245,158,11,0.6)" fontSize="15" fontFamily="Manrope,sans-serif">
            -{seg.deficiency_pct}% below
          </text>
        )}
      </motion.g>
    </g>
  );
}

// ─── MAIN DOUGHNUT COMPONENT ────────────────────────────
export function InteractiveDoughnut({ doughnut, hoveredNode, selectedNode, allNodes }) {
  const activeNode = selectedNode || hoveredNode;

  const crossCategories = useMemo(() => {
    if (!activeNode?.cross_impacts?.length || !allNodes) return new Set();
    const crossNodeIds = new Set(activeNode.cross_impacts.map(ci => ci.node_id));
    return new Set(
      allNodes.filter(n => crossNodeIds.has(n.id)).map(n => n.doughnut_category)
    );
  }, [activeNode, allNodes]);

  if (!doughnut) return null;

  const { ecological, social } = doughnut;
  const activeCategory = activeNode?.doughnut_category || null;
  const isFocus = !!selectedNode;
  const isIdle = !activeNode;

  const ecoTextR = ECO_OR + 55;
  const socTextR = SOC_IR - 45;

  return (
    <svg
      viewBox={`${-PAD} ${-PAD} ${VB + PAD * 2} ${VB + PAD * 2}`}
      className="block w-full h-full pointer-events-none"
      preserveAspectRatio="xMidYMid meet"
      data-testid="interactive-doughnut"
    >
      <defs>
        <filter id="glow-red" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feFlood floodColor="rgba(239,68,68,0.35)" result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-amber" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feFlood floodColor="rgba(245,158,11,0.35)" result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-green" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feFlood floodColor="rgba(74,222,128,0.35)" result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        {/* Curved text paths */}
        <path id="eco-ceiling-arc" d={topArcPath(CX, CY, ecoTextR)} fill="none" />
        <path id="soc-foundation-arc" d={bottomArcPath(CX, CY, socTextR)} fill="none" />
      </defs>

      {/* Safe zone ring with idle breathing */}
      <motion.circle
        cx={CX} cy={CY} r={320}
        fill="none"
        stroke="rgba(74,222,128,0.08)"
        strokeWidth="130"
        animate={isIdle
          ? { stroke: ["rgba(74,222,128,0.06)", "rgba(74,222,128,0.12)", "rgba(74,222,128,0.06)"] }
          : { stroke: "rgba(74,222,128,0.04)" }
        }
        transition={isIdle
          ? { duration: 4, repeat: Infinity, ease: "easeInOut" }
          : { duration: 0.5 }
        }
      />

      {/* Ecological ceiling segments */}
      {ecological.map((seg, i) => (
        <EcoSegment
          key={seg.id} seg={seg} index={i} total={ecological.length}
          activeCategory={activeCategory} crossCategories={crossCategories}
          isFocus={isFocus} allNodes={allNodes}
        />
      ))}

      {/* Social foundation segments */}
      {social.map((seg, i) => (
        <SocSegment
          key={seg.id} seg={seg} index={i} total={social.length}
          activeCategory={activeCategory} crossCategories={crossCategories}
          isFocus={isFocus} allNodes={allNodes}
        />
      ))}

      {/* Curved "ECOLOGICAL CEILING" label along the top arc */}
      <motion.text
        fill="rgba(255,255,255,0.35)" fontSize="24"
        fontFamily="Manrope,sans-serif" fontWeight="700" letterSpacing="6"
        animate={isIdle ? { fill: ["rgba(255,255,255,0.3)", "rgba(255,255,255,0.5)", "rgba(255,255,255,0.3)"] } : {}}
        transition={isIdle ? { duration: 4, repeat: Infinity, ease: "easeInOut" } : {}}
      >
        <textPath href="#eco-ceiling-arc" startOffset="50%" textAnchor="middle">
          ECOLOGICAL CEILING
        </textPath>
      </motion.text>

      {/* "SOCIAL FOUNDATION" centered inside the inner ring */}
      <motion.text
        x={CX} y={CY - 4}
        textAnchor="middle" dominantBaseline="central"
        fill="rgba(255,255,255,0.3)" fontSize="22"
        fontFamily="Manrope,sans-serif" fontWeight="700" letterSpacing="4"
        animate={isIdle ? { fill: ["rgba(255,255,255,0.25)", "rgba(255,255,255,0.4)", "rgba(255,255,255,0.25)"] } : {}}
        transition={isIdle ? { duration: 4, repeat: Infinity, ease: "easeInOut" } : {}}
      >
        SOCIAL
      </motion.text>
      <motion.text
        x={CX} y={CY + 22}
        textAnchor="middle" dominantBaseline="central"
        fill="rgba(255,255,255,0.3)" fontSize="22"
        fontFamily="Manrope,sans-serif" fontWeight="700" letterSpacing="4"
        animate={isIdle ? { fill: ["rgba(255,255,255,0.25)", "rgba(255,255,255,0.4)", "rgba(255,255,255,0.25)"] } : {}}
        transition={isIdle ? { duration: 4, repeat: Infinity, ease: "easeInOut" } : {}}
      >
        FOUNDATION
      </motion.text>

      {/* Attribution at the bottom of SVG */}
      <text
        x={CX} y={VB + PAD - 8}
        textAnchor="middle"
        fill="rgba(255,255,255,0.3)" fontSize="14"
        fontFamily="Manrope,sans-serif" letterSpacing="1"
        data-testid="attribution"
      >
        Credits: Kate Raworth
      </text>
    </svg>
  );
}
